import { useEffect, useState } from 'react'
import { Search, Plus, Edit, Trash2, Tag as TagIcon, Eye, EyeOff } from 'lucide-react'
import { contentApi, type ContentTag } from '../../lib/api'

const ContentTagsManage = () => {
  const [items, setItems] = useState<ContentTag[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [editing, setEditing] = useState<ContentTag | null>(null)
  const [form, setForm] = useState<{ name: string; slug: string }>({ name: '', slug: '' })
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const r = await contentApi.adminListTags({ keyword: keyword || undefined, page, page_size: pageSize })
      setItems(r.items || [])
      setTotal(r.total || 0)
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '加载失败' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [page])

  const onSubmit = async () => {
    const name = form.name.trim()
    const slug = form.slug.trim()
    if (!name) return setToast({ kind: 'error', msg: '请输入标签名称' })
    if (!slug) return setToast({ kind: 'error', msg: '请输入标签 slug' })
    try {
      if (editing) {
        await contentApi.adminUpdateTag(editing.id, { name })
        setToast({ kind: 'success', msg: '已更新' })
      } else {
        await contentApi.adminCreateTag({ name, slug })
        setToast({ kind: 'success', msg: '已新增' })
      }
      setEditing(null)
      setForm({ name: '', slug: '' })
      load()
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '保存失败' })
    }
  }

  const onEdit = (t: ContentTag) => {
    setEditing(t)
    setForm({ name: t.name, slug: t.slug })
  }

  const onToggleActive = async (t: ContentTag) => {
    try {
      await contentApi.adminUpdateTag(t.id, { is_active: t.is_active ? 0 : 1 })
      setToast({ kind: 'success', msg: t.is_active ? '已停用' : '已启用' })
      load()
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '操作失败' })
    }
  }

  const onDelete = async (id: string) => {
    if (!confirm('确认删除该标签？相关的内容关联也会被解除。')) return
    try {
      await contentApi.adminDeleteTag(id)
      setToast({ kind: 'success', msg: '已删除' })
      load()
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '删除失败' })
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {toast && (
        <div className={'fixed top-20 right-6 z-50 px-4 py-2 rounded shadow-lg text-sm ' + (toast.kind === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200')}>
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-2 text-gray-400 hover:text-gray-600">×</button>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <TagIcon className="w-6 h-6 text-green-500" />标签管理
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">维护内容标签，便于用户按主题检索</p>
      </div>

      <div className="bg-white rounded-2xl shadow-md p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">名称</label>
          <input
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="如：高血压"
            disabled={!!editing}
            className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">slug（英文唯一标识）</label>
          <input
            value={form.slug}
            onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
            placeholder="如：hypertension"
            disabled={!!editing}
            className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
          />
        </div>
        <div className="md:col-span-2 flex gap-2">
          {editing && (
            <button onClick={() => { setEditing(null); setForm({ name: '', slug: '' }) }} className="px-3 py-2 text-sm border rounded text-gray-600 hover:bg-gray-50">
              取消编辑
            </button>
          )}
          <button onClick={onSubmit} disabled={!form.name.trim() || (!editing && !form.slug.trim())} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-40">
            {editing ? <><Edit className="w-3 h-3" />保存</> : <><Plus className="w-3 h-3" />新增标签</>}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load() } }}
              placeholder="搜索标签名"
              className="border rounded px-2 py-1 text-sm"
            />
            <button onClick={() => { setPage(1); load() }} className="px-2 py-1 text-xs border rounded hover:bg-gray-50">搜索</button>
          </div>
          <span className="text-xs text-gray-400">共 {total} 个</span>
        </div>

        {loading ? (
          <div className="text-center text-sm text-gray-400 py-6">加载中…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-6">暂无标签</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 px-2">标签名</th>
                  <th className="text-left py-2 px-2">slug</th>
                  <th className="text-left py-2 px-2">使用次数</th>
                  <th className="text-left py-2 px-2">状态</th>
                  <th className="text-right py-2 px-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(t => (
                  <tr key={t.id} className="border-b last:border-b-0 hover:bg-gray-50">
                    <td className="py-2 px-2 font-medium">#{t.name}</td>
                    <td className="py-2 px-2 text-xs text-gray-500">{t.slug}</td>
                    <td className="py-2 px-2 tabular-nums">{t.use_count}</td>
                    <td className="py-2 px-2 text-xs">
                      {t.is_active ? <span className="text-green-600">启用</span> : <span className="text-gray-400">停用</span>}
                    </td>
                    <td className="py-2 px-2 text-right space-x-2 text-xs whitespace-nowrap">
                      <button onClick={() => onEdit(t)} className="text-blue-600 hover:underline">编辑</button>
                      <button onClick={() => onToggleActive(t)} className="text-yellow-600 hover:underline">
                        {t.is_active ? '停用' : '启用'}
                      </button>
                      <button onClick={() => onDelete(t.id)} className="text-red-600 hover:underline">删除</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-xs text-gray-500 mt-3">
            <span>第 {page} / {totalPages} 页</span>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 border rounded disabled:opacity-40">上一页</button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 border rounded disabled:opacity-40">下一页</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ContentTagsManage
